import { CampaignRuleMatch, type Campaign, type CampaignRule } from "@gen/tracker/v1/campaign_pb";
import { BranchTrigger } from "@gen/tracker/v1/common_pb";

import type { TKey } from "@/lib/i18n";
import { matchFieldLabel, matchOpLabel } from "@/lib/match-fields";

type T = (key: TKey, vars?: Record<string, string | number>) => string;

const MATCH: Record<CampaignRuleMatch, TKey> = {
  [CampaignRuleMatch.UNSPECIFIED]: "setup.rules.match.pick",
  [CampaignRuleMatch.COUNTRY_IN]: "setup.rules.match.country",
  [CampaignRuleMatch.COUNTRY_NOT_IN]: "setup.rules.match.notCountry",
  [CampaignRuleMatch.DEVICE]: "setup.rules.match.device",
  [CampaignRuleMatch.BOT]: "setup.rules.match.bot",
  [CampaignRuleMatch.SCHEDULE]: "setup.rules.match.schedule",
  [CampaignRuleMatch.PARAM_NOT_EQUAL]: "setup.rules.match.paramNot",
  [CampaignRuleMatch.FIELD]: "setup.rules.match.field",
};

export const TRIGGER: Partial<Record<BranchTrigger, TKey>> = {
  [BranchTrigger.BACK]: "flow.branch.back",
  [BranchTrigger.EXIT_INTENT]: "flow.branch.exit",
  [BranchTrigger.IDLE]: "flow.branch.idle",
  [BranchTrigger.SCROLL_END]: "flow.branch.scroll",
};

/**
 * A rule's condition in the editor's words — «Если страна не PL», «Если в
 * ссылке нет ключа key=…» — so the card, the click drawer and the editor say
 * the same thing. Pure.
 */
export function ruleCondition(t: T, r: CampaignRule, tz: string): { lead: string; value: string } {
  if (r.match === CampaignRuleMatch.FIELD) return { lead: `${matchFieldLabel(r.field)} ${matchOpLabel(r.op)}`, value: r.values.join(", ") };
  const lead = t(MATCH[r.match]);
  if (r.match === CampaignRuleMatch.PARAM_NOT_EQUAL) return { lead, value: `${r.paramName}=${r.paramValue}` };
  if (r.match === CampaignRuleMatch.SCHEDULE) {
    return { lead, value: `${String(r.hourFrom).padStart(2, "0")}–${String(r.hourTo || 24).padStart(2, "0")} (${tz})` };
  }
  if (r.values.length) {
    return { lead, value: r.values.map((v) => (r.match === CampaignRuleMatch.DEVICE ? t(`setup.rules.device.${v}` as TKey) : v)).join(", ") };
  }
  return { lead, value: "" };
}

/** Every offer a campaign names: its pool, its rules, its branches. Pure. */
export function campaignOfferIds(c: Pick<Campaign, "offerWeights" | "rules" | "branches">): string[] {
  return [...c.offerWeights.map((w) => w.offerId), ...c.rules.map((r) => r.offerId), ...c.branches.flatMap((b) => b.offerWeights.map((w) => w.offerId))].filter(Boolean);
}

/** Every lander a campaign names: its pool, its rules, its branches, its thank-you page. Pure. */
export function campaignLanderIds(c: Pick<Campaign, "landerWeights" | "rules" | "branches" | "thankYouLanderId">): string[] {
  return [
    ...c.landerWeights.map((w) => w.landerId),
    ...c.rules.map((r) => r.landerId),
    ...c.branches.flatMap((b) => b.landerWeights.map((w) => w.landerId)),
    c.thankYouLanderId,
  ].filter(Boolean);
}

/** The rule a click took, by the node it was resolved at — undefined when it went by the shares. Pure. */
export function ruleOfNode(c: Pick<Campaign, "rules">, nodeId: string): number | undefined {
  if (!nodeId) return undefined;
  const i = c.rules.findIndex((r) => r.nodeId === nodeId);
  return i >= 0 ? i : undefined;
}
